import React, { useEffect, useRef, useState } from "react";
import { Bot, Eye } from "lucide-react";

/**
 * AI Mentor panel with typewriter effect on incoming messages.
 * Receives a list of { id, text, kind } messages controlled by parent.
 */
export const AIMentorPanel = ({ messages, observing = true, isLoading = false }) => {
  const listRef = useRef(null);
  const [typedMap, setTypedMap] = useState({});

  // Typewriter: progressively reveal text for new messages
  useEffect(() => {
    messages.forEach((m) => {
      if (typedMap[m.id] !== undefined && typedMap[m.id] >= m.text.length) return;
      let chars = typedMap[m.id] || 0;
      const interval = setInterval(() => {
        chars += 2;
        setTypedMap((prev) => ({ ...prev, [m.id]: Math.min(m.text.length, chars) }));
        if (chars >= m.text.length) clearInterval(interval);
      }, 22);
      return () => clearInterval(interval);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, typedMap]);

  return (
    <div
      className="flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0a0b14]"
      data-testid="sim-mentor-panel"
    >
      <div className="flex shrink-0 items-center justify-between border-b border-white/10 bg-black/30 px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-amber-400/30 bg-amber-400/10">
            <Bot className="h-3.5 w-3.5 text-amber-300" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/80">
            ◈ RE₿EL AI Mentor
          </span>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] text-emerald-300">
          <span className="relative h-1 w-1">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/80" />
            <span className="relative inline-flex h-1 w-1 rounded-full bg-emerald-400" />
          </span>
          {observing ? "Observando" : "Pausa"}
          <Eye className="h-2.5 w-2.5" />
        </span>
      </div>

      <div
        ref={listRef}
        className="flex-1 space-y-3 overflow-y-auto px-4 py-4 text-sm leading-relaxed"
        data-testid="sim-mentor-feed"
      >
        {messages.length === 0 && !isLoading && (
          <p className="text-xs italic text-white/40">Esperando primera observación institucional…</p>
        )}
        {messages.map((m) => {
          const shown = typedMap[m.id] ?? 0;
          const done = shown >= m.text.length;
          return (
            <div key={m.id} className="rounded-lg border border-white/5 bg-black/30 p-3" data-testid={`mentor-msg-${m.id}`}>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300/80">
                {m.label || "Observación"}
              </p>
              <p className="mt-1.5 font-mono text-[13px] text-white/85">
                {m.text.slice(0, shown)}
                {!done && (
                  <span
                    className="ml-0.5 inline-block h-3 w-0.5 align-middle"
                    style={{ background: "#f5a623", animation: "blink-cursor 0.8s infinite" }}
                  />
                )}
              </p>
            </div>
          );
        })}
        {isLoading && (
          <div className="flex items-center gap-2 text-[11px] text-amber-300/80">
            <span className="flex gap-1">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400"
                  style={{ animation: "pulse-dot 1.2s infinite", animationDelay: `${i * 0.18}s` }}
                />
              ))}
            </span>
            <span className="uppercase tracking-[0.22em]">Analizando…</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIMentorPanel;
